import React, { useState } from "react";
import { Col, Row, Upload, Button, Card, Alert, Tag, Collapse, Descriptions } from "antd";
import { InboxOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { ComponentTitle, StledSpin } from "../../custom-components";
import { authAxios } from "../../utils/session_utils";

const { Dragger } = Upload;

interface ValidationWarning {
  rule: string;
  severity: "error" | "warning";
  message: string;
  rowIndex?: number;
}

interface ValidationSummary {
  totalDurationSeconds: number;
  totalDurationFormatted: string;
  totalRows: number;
  contentRows: number;
  adRows: number;
}

interface ValidationResult {
  fileName: string;
  passed: boolean;
  warnings: ValidationWarning[];
  summary: ValidationSummary;
}

interface ApiResponse {
  passed: boolean;
  results: ValidationResult[];
}

const CsvValidationComponent: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ValidationResult[] | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);

  const handleValidate = async () => {
    if (fileList.length === 0) return;

    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append("files", file.originFileObj || file);
    });

    setLoading(true);
    try {
      const response = await authAxios({
        method: "POST",
        url: `${process.env.REACT_APP_BACK_END_API}/scheduling/validate-csv`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data: ApiResponse = response.data;
      setResults(data.results);
    } catch (err: any) {
      console.error("Validation error:", err);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const severityIcon = (severity: string) =>
    severity === "error" ? <CloseCircleOutlined style={{ color: "#ff4d4f" }} /> : <WarningOutlined style={{ color: "#faad14" }} />;

  return (
    <StledSpin spinning={loading}>
      <Row justify="center">
        <Col>
          <ComponentTitle>CSV Schedule Validation</ComponentTitle>
        </Col>
      </Row>

      <Row justify="center" style={{ marginTop: 16 }}>
        <Col xs={24} sm={22} md={18} lg={14}>
          <Card
            actions={[
              <Button
                key="validate-btn"
                type="primary"
                onClick={handleValidate}
                disabled={fileList.length === 0}
              >
                Validate
              </Button>,
            ]}
          >
            <Dragger
              multiple
              accept=".csv"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: newList }) => {
                setFileList(newList);
                setResults(null);
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag CSV files to upload</p>
              <p className="ant-upload-hint">
                Upload one or more Amagi schedule CSVs to validate against business rules.
              </p>
            </Dragger>
          </Card>
        </Col>
      </Row>

      {results && (
        <Row justify="center" style={{ marginTop: 24 }}>
          <Col xs={24} sm={22} md={18} lg={14}>
            {results.map((result, idx) => (
              <Card
                key={idx}
                title={
                  <span>
                    {result.passed ? (
                      <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                    )}
                    {result.fileName}
                    <Tag
                      color={result.passed ? "success" : "error"}
                      style={{ marginLeft: 12 }}
                    >
                      {result.passed ? "PASSED" : "FAILED"}
                    </Tag>
                  </span>
                }
                style={{ marginBottom: 16 }}
              >
                <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Total Duration">
                    {result.summary.totalDurationFormatted}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Rows">
                    {result.summary.totalRows}
                  </Descriptions.Item>
                  <Descriptions.Item label="Content Rows">
                    {result.summary.contentRows}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ad Rows">
                    {result.summary.adRows}
                  </Descriptions.Item>
                </Descriptions>

                {result.warnings.length === 0 ? (
                  <Alert message="All checks passed" type="success" showIcon />
                ) : (
                  <Collapse
                    defaultActiveKey={["warnings"]}
                    items={[
                      {
                        key: "warnings",
                        label: `${result.warnings.length} issue${result.warnings.length > 1 ? "s" : ""} found`,
                        children: result.warnings.map((w, wIdx) => (
                          <Alert
                            key={wIdx}
                            message={
                              <span>
                                {severityIcon(w.severity)}{" "}
                                <Tag color={w.severity === "error" ? "error" : "warning"} style={{ marginLeft: 4 }}>
                                  {w.rule}
                                </Tag>{" "}
                                {w.message}
                              </span>
                            }
                            type={w.severity === "error" ? "error" : "warning"}
                            style={{ marginBottom: 8 }}
                          />
                        )),
                      },
                    ]}
                  />
                )}
              </Card>
            ))}
          </Col>
        </Row>
      )}
    </StledSpin>
  );
};

export default CsvValidationComponent;
